import { useOnboardingDataSlice } from "@redux/slices/OnboardingDataSlice";
import {
  clearFavCollectionVisible,
  clearListViewCustomisation,
  clearSplitFilterVisible,
  resetPostOnboardingCheck,
  setIsCompanyTheme,
  setNavigationCustomisationLoad,
  setPDPCustomisationLoad,
  setSearchFilterCustomisationLoad,
  setSearchResultCustomisationLoad,
  setUnifiedProductPagesLoad,
} from "@redux/slices/appStateSlice";
import { saveNavigationData } from "@redux/slices/navigationMenuSlice";
import { clearOtherGroup } from "@redux/slices/otherSearchGroups";
import { setPdpLayout } from "@redux/slices/pdpLayoutSlice";
import {
  resetAuxPayload,
  resetFiltersPayload,
  setFiltersData,
} from "@redux/slices/searchFilters";
import { clearGroup } from "@redux/slices/searchGroupsSlice";
import { setCustomSearchData } from "@redux/slices/searchResultsCustomization";
import { setTheme } from "@redux/slices/themeSlice";
import { setTypography } from "@redux/slices/typographySlice";
import {
  clearSearchResultViewType,
  clearUserInfo,
  setCompanyLocations,
} from "@redux/slices/userInfoSlice";
import { useDispatch, useSelector } from "react-redux";
import { removeSecureStoreItems } from "../../../utils";
import { useQueryClient } from "@tanstack/react-query";
import { setGlobalData } from "@redux/slices/globalSlice";
import { setUnifiedProductPages } from "@redux/slices/unifiedProductPages";
import { Linking, Platform } from "react-native";
import { resetAdBannerTime } from "@utils/product/productValidators";
import { setTranslationKeys } from "@redux/slices/translationSlice";
import { OneSignal } from "react-native-onesignal";
import IntercomClient from "@utils/IntercomClient";
import { resetShippingData } from "@redux/slices/orderCartSlice";
import { useAdBatchTracking } from "../../../hooks/useAdBatchTracking";

const useLogout = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const isWeb = process.env.EXPO_PUBLIC_PLATFORM === "web";
  const { sendBatch } = useAdBatchTracking();
  if (isWeb) {
    IntercomClient.logout();
  }
  const isCompanyThemeLoaded = useSelector(
    (state: any) => state.appState?.isCompanyThemeLoaded
  );
  const isNavigationCustomisationLoad = useSelector(
    (state: any) => state.appState?.isNavigationCustomisationLoad
  );
  const isSearchFilterCustomisationLoad = useSelector(
    (state: any) => state.appState?.isSearchFilterCustomisationLoad
  );
  const isSearchResultCustomisationLoad = useSelector(
    (state: any) => state.appState?.isSearchResultCustomisationLoad
  );
  const isPDPCustomisationLoad = useSelector(
    (state: any) => state.appState?.isPDPCustomisationLoad
  );
  const isUnifiedProductPagesLoad = useSelector(
    (state: any) => state.appState?.isUnifiedProductPagesLoad
  );
  const orgTheme = useSelector((state: any) => state.appState?.orgTheme);
  const orgTypography = useSelector(
    (state: any) => state.appState?.orgTypography
  );
  const orgPDPCustomisation = useSelector(
    (state: any) => state.appState?.orgPDPCustomisation
  );
  const orgNavigationCustomisation = useSelector(
    (state: any) => state.appState?.orgNavigationCustomisation
  );
  const orgSearchFilterCustomisation = useSelector(
    (state: any) => state.appState?.orgSearchFilterCustomisation
  );
  const orgSearchResultCustomisation = useSelector(
    (state: any) => state.appState?.orgSearchResultCustomisation
  );
  const orgUnifiedProductPagesCustomisation = useSelector(
    (state: any) => state.appState?.orgUnifiedProductPagesCustomisation
  );
  const orgGlobalCustomization = useSelector(
    (state: any) => state.appState?.orgGlobalCustomization
  );
  const orgTranslationKeys = useSelector(
    (state: any) => state.appState?.orgTranslationKeys
  );
  const isGlobalCustomisationLoad = useSelector(
    (state: any) => state.appState?.isGlobalCustomisationLoad
  );
  const { clearPostOnboardingData, clearPostOnboardingTimeStamp } =
    useOnboardingDataSlice();

  const handleLogout = async () => {
    if (isCompanyThemeLoaded) {
      dispatch(setIsCompanyTheme(false));
    }

    if (isNavigationCustomisationLoad) {
      dispatch(setNavigationCustomisationLoad(false));
    }

    if (isPDPCustomisationLoad) {
      dispatch(setPDPCustomisationLoad(false));
    }

    if (isSearchFilterCustomisationLoad) {
      dispatch(setSearchFilterCustomisationLoad(false));
    }

    if (isSearchResultCustomisationLoad) {
      dispatch(setSearchResultCustomisationLoad(false));
    }

    if (isUnifiedProductPagesLoad) {
      dispatch(setUnifiedProductPagesLoad(false));
    }

    if (orgPDPCustomisation) {
      dispatch(setPdpLayout(orgPDPCustomisation));
    }

    if (orgNavigationCustomisation) {
      dispatch(saveNavigationData(orgNavigationCustomisation));
    }
    if (isGlobalCustomisationLoad && orgGlobalCustomization) {
      dispatch(setGlobalData(orgGlobalCustomization));
    }

    if (orgSearchFilterCustomisation) {
      dispatch(setFiltersData(orgSearchFilterCustomisation));
    }

    if (orgSearchResultCustomisation) {
      dispatch(setCustomSearchData(orgSearchResultCustomisation));
    }

    if (orgUnifiedProductPagesCustomisation) {
      dispatch(setUnifiedProductPages(orgUnifiedProductPagesCustomisation));
    }

    if (orgTheme) {
      dispatch(setTheme(orgTheme));
    }

    if (orgTypography) {
      dispatch(setTypography(orgTypography));
    }
    if (orgTranslationKeys) {
      dispatch(setTranslationKeys(orgTranslationKeys));
    }

    if (Platform.OS === "web") {
      localStorage.removeItem("userData");
      localStorage.removeItem("installation_identifier");
    }

    if (isWeb && Platform.OS === "web") {
      if (typeof window !== "undefined" && (window as any).DD_RUM?.setUser) {
        (window as any).DD_RUM.setUser({});
      }
    }

    clearPostOnboardingData();
    dispatch(resetAuxPayload());
    dispatch(resetFiltersPayload());
    dispatch(resetPostOnboardingCheck());
    clearPostOnboardingTimeStamp();
    dispatch(clearUserInfo());
    dispatch(clearSearchResultViewType());
    dispatch(setCompanyLocations([]));
    dispatch(resetShippingData());
    dispatch(clearGroup());
    dispatch(clearOtherGroup());
    dispatch(clearListViewCustomisation());
    dispatch(clearSplitFilterVisible());
    dispatch(clearFavCollectionVisible());
    await sendBatch();
    await resetAdBannerTime();
    await removeSecureStoreItems(["token"]);
    await removeSecureStoreItems(["vdbchat_conversations"]);
    queryClient.removeQueries();
    if (Platform.OS !== "web") {
      OneSignal.logout();
      IntercomClient.logout();
      Linking.openURL(`${process.env.EXPO_PUBLIC_APP_SCHEME}://`);
    }

    // TODO: this accountDeleted should be added in an upper level in the project and the functions to set params should be handled via Provider
    // navigation.dispatch(
    //   CommonActions.reset({
    //     index: 0,
    //     routes: [
    //       {
    //         name: Routes.AuthNavigator,
    //         state: {
    //           index: 0,
    //           routes: [
    //             {
    //               name:
    //                 isTablet || isDesktop
    //                   ? Routes.OnboardingWeb
    //                   : Routes.Authentication,
    //               params: {
    //                 accountDeleted,
    //               },
    //             },
    //           ],
    //         },
    //       },
    //     ],
    //   })
    // );
  };
  return handleLogout;
};

export default useLogout;
